import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { promptVersionsApi } from '@/api/prompt-versions';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ACTIVE = '__active__';

export function PromptVersionSelect({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (version: number | null) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { currentOrgId, currentBotId } = useWorkspace();
  const versions = useQuery({
    queryKey: ['prompt-versions', currentBotId, 'select'],
    queryFn: () => promptVersionsApi.list(currentBotId!, { limit: 50 }),
    enabled: !!currentOrgId && !!currentBotId,
    staleTime: 30_000,
  });

  const items = versions.data?.items ?? [];
  const activeVersion = versions.data?.activeVersion ?? null;

  React.useEffect(() => {
    if (value !== null && value === activeVersion) onChange(null);
  }, [value, activeVersion, onChange]);

  if (items.length === 0 && value === null) return null;

  return (
    <div className="space-y-1.5">
      <Label>{t('promptVersions.select.label')}</Label>
      <Select
        value={value === null ? ACTIVE : String(value)}
        onValueChange={(v) => onChange(v === ACTIVE ? null : Number(v))}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ACTIVE}>
            {activeVersion
              ? t('promptVersions.select.activeWithVersion', { version: activeVersion })
              : t('promptVersions.select.active')}
          </SelectItem>
          {value !== null && !items.some((v) => v.version === value) ? (
            <SelectItem value={String(value)}>v{value}</SelectItem>
          ) : null}
          {items
            .filter((v) => v.version !== activeVersion)
            .map((v) => (
              <SelectItem key={v.version} value={String(v.version)}>
                <span className="font-mono">v{v.version}</span>
                {v.note ? <span className="text-muted-foreground"> — {v.note}</span> : null}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      {value !== null ? (
        <p className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
          <FlaskConical className="h-3 w-3" />
          {t('promptVersions.select.testHint', { version: value })}
        </p>
      ) : (
        <p className="text-[10px] text-muted-foreground">{t('promptVersions.select.hint')}</p>
      )}
    </div>
  );
}
